import { useState } from 'react';
import styles from './ResponseCard.module.css';

/**
 * 응답 카드 4섹션 (기획 03 §6.3).
 * - 즉시조치: 펼침 기본 (가장 우선)
 * - 법률 검토 / 유사 사례 / 다음 단계: 접힘 기본
 */
export function ResponseCard({ card, precedents = [] }) {
  if (!card) return null;

  const immediate = card.immediate_actions || [];
  const review = card.legal_review || {};
  const similar = card.similar_cases || [];
  const next = card.next_steps || {};

  return (
    <article className={styles.root}>
      <Section title="즉시 조치" defaultOpen={true} count={immediate.length}>
        {immediate.length === 0 ? (
          <p className={styles.empty}>즉시 조치 항목이 없습니다.</p>
        ) : (
          <ul className={styles.actionList}>
            {immediate.map((a, i) => (
              <li key={i} className={styles.action}>
                <PriorityTag priority={a.priority} />
                <div className={styles.actionBody}>
                  {a.title && <div className={styles.actionTitle}>{a.title}</div>}
                  {a.detail && <div className={styles.actionDetail}>{a.detail}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="법률 검토">
        {review.summary && <p className={styles.reviewSummary}>{review.summary}</p>}
        {Array.isArray(review.key_factors) && review.key_factors.length > 0 && (
          <ul className={styles.factorList}>
            {review.key_factors.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        )}
        {!review.summary && !(review.key_factors || []).length && (
          <p className={styles.empty}>검토 내용이 비어있습니다.</p>
        )}
      </Section>

      <Section title="유사 사례" count={similar.length}>
        {similar.length === 0 ? (
          <p className={styles.empty}>매칭된 판례가 없습니다.</p>
        ) : (
          <ul className={styles.precedentList}>
            {similar.map((c, i) => {
              const meta = precedents.find((p) => p.case_number === c.case_number);
              return (
                <li key={i} className={styles.precedent}>
                  <div className={styles.precedentHeader}>
                    <span className={styles.caseNumber}>{c.case_number}</span>
                    <FavorTag favorable={c.favorable_for_doctor} />
                  </div>
                  {c.relevance && <p className={styles.relevance}>{c.relevance}</p>}
                  {meta?.excerpt && (
                    <details className={styles.excerptDetails}>
                      <summary>본문 발췌</summary>
                      <div className={styles.excerpt}>{meta.excerpt}</div>
                    </details>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="다음 단계">
        {next.summary && <p>{next.summary}</p>}
        {next.recommend_consult && (
          <div className={styles.consultBlock}>
            <button type="button" className={styles.consultBtn}>
              변호사 상담 신청
            </button>
            {next.consult_reason && <p className={styles.consultReason}>{next.consult_reason}</p>}
          </div>
        )}
        {!next.summary && !next.recommend_consult && (
          <p className={styles.empty}>다음 단계가 비어있습니다.</p>
        )}
      </Section>
    </article>
  );
}

function Section({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const headerId = `card-section-${title.replace(/\s+/g, '-')}`;
  return (
    <section className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${headerId}-body`}
      >
        <span className={styles.sectionTitle}>{title}</span>
        {typeof count === 'number' && count > 0 && (
          <span className={styles.sectionCount}>{count}</span>
        )}
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div id={`${headerId}-body`} className={styles.sectionBody}>
          {children}
        </div>
      )}
    </section>
  );
}

function PriorityTag({ priority }) {
  if (!priority) return <span className={styles.priorityPad} aria-hidden="true" />;
  const label = { high: '높음', medium: '보통', low: '낮음' }[priority] || priority;
  return (
    <span className={`${styles.priority} ${styles[`priority_${priority}`] || ''}`}>{label}</span>
  );
}

function FavorTag({ favorable }) {
  if (favorable === true) {
    return <span className={`${styles.favor} ${styles.favorYes}`}>의료인 유리</span>;
  }
  if (favorable === false) {
    return <span className={`${styles.favor} ${styles.favorNo}`}>의료인 불리</span>;
  }
  return null;
}
